import { PettyWorkStatus } from "features/PettyWorks/constants/petty-work.constants";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import type { PettyWork } from "features/PettyWorks/types/petty-work.types";

const PW_CANCELLABLE_STATUSES: ReadonlySet<string> = new Set([
  PettyWorkStatus.NEW,
  PettyWorkStatus.IN_PROGRESS,
  PettyWorkStatus.ON_HOLD,
]);

export type PettyWorkLifecycleVisibility = {
  start: boolean;
  hold: boolean;
  resume: boolean;
  /** IN_PROGRESS → READY_FOR_BILLING (no separate completion-approval step). */
  approveForBilling: boolean;
  /** READY_FOR_BILLING → COMPLETED */
  submitBilling: boolean;
  cancel: boolean;
  /** COMPLETED only — cancels the current invoice and generates a corrected one. */
  regenerateInvoice: boolean;
};

/** Which lifecycle actions are allowed for this record (permissions + status). */
export function getPettyWorkLifecycleVisibility(
  pettyWork: Pick<PettyWork, "status">,
  can: (permission: string) => boolean,
): PettyWorkLifecycleVisibility {
  const raw = String(pettyWork.status ?? "").trim();
  return {
    start: can(Permissions.PETTY_WORK.START) && raw === PettyWorkStatus.NEW,
    hold: can(Permissions.PETTY_WORK.HOLD) && raw === PettyWorkStatus.IN_PROGRESS,
    resume: can(Permissions.PETTY_WORK.RESUME) && raw === PettyWorkStatus.ON_HOLD,
    approveForBilling:
      can(Permissions.PETTY_WORK.APPROVE) && raw === PettyWorkStatus.IN_PROGRESS,
    submitBilling:
      can(Permissions.PETTY_WORK.SUBMIT_BILLING) &&
      raw === PettyWorkStatus.READY_FOR_BILLING,
    cancel: can(Permissions.PETTY_WORK.CANCEL) && PW_CANCELLABLE_STATUSES.has(raw),
    regenerateInvoice:
      can(Permissions.PETTY_WORK.REGENERATE_INVOICE) &&
      raw === PettyWorkStatus.COMPLETED,
  };
}

export function pettyWorkLifecycleAnyVisible(
  v: PettyWorkLifecycleVisibility,
): boolean {
  return (
    v.start ||
    v.hold ||
    v.resume ||
    v.approveForBilling ||
    v.submitBilling ||
    v.cancel ||
    v.regenerateInvoice
  );
}
